import { DesktopIcon, AccessIcon, SearchIcon } from "@bp/ui-components";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  globalSearch,
  type SearchData,
  type SearchKind,
  type SearchResult,
} from "../../utils/globalSearch";
import { Avatar } from "../Avatar/Avatar";
import { OrgMark } from "../OrgMark/OrgMark";
import styles from "./CommandSearch.module.css";

const KIND_LABEL: Record<SearchKind, string> = {
  person: "Benutzer",
  org: "Organisation",
  client: "OAuth-Client",
  app: "Application",
};

function routeFor(result: SearchResult): string {
  switch (result.kind) {
    case "person":
      return `/users/${result.id}`;
    case "org":
      return `/organizations/${result.id}`;
    case "client":
      return `/auth-clients/${result.id}`;
    case "app":
      return `/applications/${result.id}`;
  }
}

function highlight(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) {
    return text;
  }
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) {
    return text;
  }
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

function resultIcon(result: SearchResult): ReactNode {
  if (result.kind === "person") {
    return <Avatar name={result.title} size={30} />;
  }
  if (result.kind === "org") {
    return <OrgMark size={30} />;
  }
  return (
    <span
      style={{
        width: 30,
        height: 30,
        borderRadius: "var(--border-radius-m)",
        background: "var(--color-primary-lighter)",
        color: "var(--color-primary-dark)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {result.kind === "app" ? (
        <DesktopIcon style={{ fontSize: 15 }} />
      ) : (
        <AccessIcon style={{ fontSize: 15 }} />
      )}
    </span>
  );
}

type CommandSearchProps = {
  data: SearchData;
};

export function CommandSearch({ data }: CommandSearchProps) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const results = useMemo(() => globalSearch(q, data), [q, data]);

  useEffect(() => {
    setCur(0);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const go = (result: SearchResult) => {
    setOpen(false);
    setQ("");
    navigate(routeFor(result));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCur((c) => Math.min(results.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCur((c) => Math.max(0, c - 1));
    } else if (e.key === "Enter" && results[cur]) {
      go(results[cur]);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className={styles.cmd} ref={rootRef}>
      <div
        className={open ? `${styles.field} ${styles.fieldFocus}` : styles.field}
        onClick={() => inputRef.current?.focus()}
        onKeyDown={undefined}
        role="presentation"
      >
        <SearchIcon style={{ fontSize: 17, color: "var(--color-grey)" }} />
        <input
          ref={inputRef}
          className={styles.input}
          placeholder="Person, Schule oder Client suchen …"
          value={q}
          aria-label="Globale Suche"
          aria-expanded={open}
          aria-controls={listId}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {!open && <span className={styles.kbd}>⌘K</span>}
      </div>
      {open && q.trim() !== "" && (
        <div className={styles.pop} id={listId} role="listbox">
          {results.length > 0 ? (
            <>
              <div className={styles.sec}>{results.length} Treffer</div>
              {results.map((r, i) => (
                <button
                  type="button"
                  key={`${r.kind}-${r.id}`}
                  className={i === cur ? `${styles.res} ${styles.resCur}` : styles.res}
                  role="option"
                  aria-selected={i === cur}
                  onMouseEnter={() => setCur(i)}
                  onClick={() => go(r)}
                >
                  {resultIcon(r)}
                  <span className={styles.resMain}>
                    <span className={styles.resTitle}>{highlight(r.title, q)}</span>
                    <span className={styles.resSub}>{highlight(r.sub, q)}</span>
                  </span>
                  <span className={styles.resKind}>{KIND_LABEL[r.kind]}</span>
                </button>
              ))}
            </>
          ) : (
            <div className={styles.empty}>Kein Treffer für „{q}".</div>
          )}
        </div>
      )}
    </div>
  );
}
